import { useCallback, useState } from 'react'
import { Routes, Route, NavLink, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  Calendar,
  Map as MapIcon,
  Mic,
  MoreHorizontal,
  ChevronDown,
  ListChecks,
  Users,
  Tag,
  FileCode,
  Bot,
  Megaphone,
} from 'lucide-react'
import EventList from './components/EventList.jsx'
import Footer from './components/Footer.jsx'
import MapPage from './pages/MapPage.jsx'
import SignInPage from './pages/SignInPage.jsx'
import OAuthCallbackPage from './pages/OAuthCallbackPage.jsx'
import RegisterPage from './pages/RegisterPage.jsx'
import VerifyPage from './pages/VerifyPage.jsx'
import DashboardPage from './pages/DashboardPage.jsx'
import BookSpeakerPage from './pages/BookSpeakerPage.jsx'
import AdminBookingsPage from './pages/AdminBookingsPage.jsx'
import MyEventsPage from './pages/MyEventsPage.jsx'
import AdminEventsPage from './pages/AdminEventsPage.jsx'
import EditEventPage from './pages/EditEventPage.jsx'
import AdminUsersPage from './pages/AdminUsersPage.jsx'
import AdminTagsPage from './pages/AdminTagsPage.jsx'
import AdminMessagesPage from './pages/AdminMessagesPage.jsx'
import ConferenceDetailPage from './pages/ConferenceDetailPage.jsx'
import EventDetailPage from './pages/EventDetailPage.jsx'
import ContractPage from './pages/ContractPage.jsx'
import LinkDevicePage from './pages/LinkDevicePage.jsx'
import McpSetupPage from './pages/McpSetupPage.jsx'
import MacosInstallerPage from './pages/MacosInstallerPage.jsx'
import AuthGuard from './components/AuthGuard.jsx'
import HeaderUserBadge from './components/HeaderUserBadge.jsx'
import HelpButton from './components/HelpButton.jsx'
import useMe from './hooks/useMe.js'
import useTokenRefresh from './hooks/useTokenRefresh.js'
import useProfileHydration from './hooks/useProfileHydration.js'
import SessionExpiredModal from './components/SessionExpiredModal.jsx'
import { PAGE_STANDARD } from './lib/layout.js'
import { eventCountStrapline } from './lib/text.js'
import useFilterRoute from './hooks/useFilterRoute.js'
import NotFound from './components/NotFound.jsx'

function navLinkClass({ isActive }) {
  return [
    'inline-flex min-h-[44px] shrink-0 items-center gap-1.5 px-1 text-sm transition',
    isActive
      ? 'text-brand-700 font-medium border-b-2 border-brand-600'
      : 'text-slate-600 hover:text-brand-600 border-b-2 border-transparent',
  ].join(' ')
}

function Home() {
  // #338: the year lives in the path (`/2026`) and every other filter in the
  // query string, so this page holds no filter state of its own.
  const { year, invalidYear, filters, setFilters } = useFilterRoute('')
  const [count, setCount] = useState(null)
  const [pastIncluded, setPastIncluded] = useState(false)
  const onCountChange = useCallback((n, meta) => {
    setCount(n)
    setPastIncluded(Boolean(meta?.pastIncluded))
  }, [])

  // `/banana` matches this route too. Say so, rather than rendering an empty
  // calendar that reads as "nothing scheduled".
  if (invalidYear) return <NotFound />

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-12`}>
      <header className="mb-6 sm:mb-10">
        <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-5xl">
          {/* A year view is a look back as much as forward, so "speaking next"
              would be a promise it does not keep. */}
          {year ? (
            <>
              <span className="brand-gradient-text">The Office of the CTO</span>
              <br className="hidden sm:block" />
              <span className="text-slate-900">{` in ${year}`}</span>
            </>
          ) : (
            <>
              <span className="brand-gradient-text">Where the Office of the CTO</span>
              <br className="hidden sm:block" />
              <span className="text-slate-900"> is speaking next</span>
            </>
          )}
        </h1>
        <p className="mt-3 text-base text-slate-600 sm:text-lg">
          {eventCountStrapline(count, pastIncluded)}
        </p>
      </header>

      <EventList onCountChange={onCountChange} filters={filters} onFiltersChange={setFilters} />
    </main>
  )
}

function MoreMenu() {
  const { me } = useMe()
  const navigate = useNavigate()
  // Secondary items collapse into a single dropdown so the header stays
  // narrow. Contract is always visible; auth-gated items appear when /me
  // resolves to a speaker or admin.
  const items = []
  if (me) {
    const eventsLabel = me.role === 'ADMIN' ? 'All events' : 'My events'
    items.push({ to: '/admin/events', label: eventsLabel, Icon: ListChecks })
    if (me.role === 'ADMIN') {
      items.push({ to: '/admin/users', label: 'Users', Icon: Users })
      items.push({ to: '/admin/tags', label: 'Tags', Icon: Tag })
      items.push({ to: '/admin/messages', label: 'Messages', Icon: Megaphone })
    }
  }
  items.push({ to: '/contract', label: 'Data contract', Icon: FileCode })
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-full px-2 text-sm text-slate-600 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 data-[state=open]:text-brand-700"
        aria-label="More"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">More</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-40 min-w-[12rem] rounded-xl border border-slate-200 bg-white p-1 text-sm text-slate-700 shadow-lg"
        >
          {items.map(({ to, label, Icon }) => (
            <DropdownMenu.Item
              key={to}
              onSelect={() => navigate(to)}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 outline-none data-[highlighted]:bg-brand-50 data-[highlighted]:text-brand-800"
            >
              <Icon className="h-4 w-4 text-slate-500" aria-hidden="true" />
              <span>{label}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

// Routes /admin/events to the right view depending on the caller's role.
// Admins get the table-with-filters from #37; speakers get the simpler
// per-speaker list from #36.
function EventsLanding() {
  const { me, loading } = useMe()
  if (loading || !me) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      </main>
    )
  }
  if (me.role === 'VIEWER') return <Navigate to="/" replace />
  return me.role === 'ADMIN' ? <AdminEventsPage /> : <MyEventsPage />
}

// Routes that render without the surrounding chrome (header, footer, help button).
// Sign-in is intentionally trapped — the user is in the middle of an OAuth flow
// (popup window from claude.ai or similar), and any nav element would let them
// strand the OAuth attempt. Match the locked-down look of vendor sign-in pages.
const NO_CHROME_PATHS = new Set(['/signin', '/oauth/callback'])

export default function App() {
  const location = useLocation()
  useTokenRefresh()
  useProfileHydration()
  const noChrome = NO_CHROME_PATHS.has(location.pathname)
  if (noChrome) {
    return (
      <div className="flex min-h-screen flex-col">
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
        </Routes>
        <SessionExpiredModal />
      </div>
    )
  }
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className={`${PAGE_STANDARD} flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-3 sm:py-4`}>
          <Link to="/" className="inline-flex shrink-0 items-center gap-2 font-semibold tracking-tight text-brand-700">
            <img src="/favicon.svg" alt="" aria-hidden="true" className="h-7 w-7 rounded-lg shadow-sm" />
            <span>Next</span>
            <span className="hidden text-xs font-normal text-slate-500 sm:inline">by Actian Labs</span>
          </Link>
          <nav className="flex min-w-0 flex-1 items-center justify-end gap-x-2 text-sm sm:gap-x-3">
            <NavLink to="/" end className={navLinkClass} aria-label="Calendar">
              <Calendar className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Calendar</span>
            </NavLink>
            <NavLink to="/map" className={navLinkClass} aria-label="Map">
              <MapIcon className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Map</span>
            </NavLink>
            <NavLink to="/book" className={navLinkClass} aria-label="Book a speaker">
              <Mic className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Book</span>
            </NavLink>
            <NavLink to="/help/mcp-setup" className={navLinkClass} aria-label="Connect Claude">
              <Bot className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Claude</span>
            </NavLink>
            <MoreMenu />
            <span className="ml-1 shrink-0 sm:ml-2"><HeaderUserBadge /></span>
          </nav>
        </div>
      </header>

      <Routes>
        <Route path="/" element={<Home />} />
        {/* #338: /2026, /2025 … A four-digit segment that is not a plausible
            year falls through to NotFound inside Home rather than rendering an
            empty calendar. Static routes below still win over this one —
            react-router ranks a literal segment above a dynamic one. */}
        <Route path="/:year" element={<Home />} />
        <Route path="/events/:id" element={<EventDetailPage />} />
        <Route path="/conferences/:id" element={<ConferenceDetailPage />} />
        <Route path="/contract" element={<ContractPage />} />
        <Route path="/help/mcp-setup" element={<McpSetupPage />} />
        <Route path="/macos-installer" element={<MacosInstallerPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/map/:year" element={<MapPage />} />
        <Route path="/book" element={<BookSpeakerPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/link" element={<LinkDevicePage />} />
        <Route path="/admin" element={<AuthGuard><DashboardPage /></AuthGuard>} />
        <Route path="/admin/bookings" element={<AuthGuard><AdminBookingsPage /></AuthGuard>} />
        <Route path="/admin/events" element={<AuthGuard><EventsLanding /></AuthGuard>} />
        <Route path="/admin/events/new" element={<AuthGuard><EditEventPage /></AuthGuard>} />
        <Route path="/admin/events/:id" element={<AuthGuard><EditEventPage /></AuthGuard>} />
        <Route path="/admin/users" element={<AuthGuard><AdminUsersPage /></AuthGuard>} />
        <Route path="/admin/tags" element={<AuthGuard><AdminTagsPage /></AuthGuard>} />
        <Route path="/admin/messages" element={<AuthGuard><AdminMessagesPage /></AuthGuard>} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <div className="mt-auto">
        <Footer />
      </div>

      <HelpButton />
      <SessionExpiredModal />
    </div>
  )
}
